<script setup lang="ts">
import { computed, inject } from 'vue'

import Icon from '../icon/Icon.vue'
import type { TIconName } from '../icon/types'
import { tabsheetGroupKey } from './types'

const props = defineProps<{
  /**
   * Optional icon to show alongside the text content.
   */
  icon?: TIconName
  /**
   * A text string that is used as the tab label
   */
  label: string

  /**
   * The value this tab represents. Assigned by the TabGroup controller, its
   * used to determine whether the tab is active.
   */
  value: number
}>()

const ctx = inject(tabsheetGroupKey, null)

// If we didn't get a context reference then this component wasn't mounted
// inside a `<TabSheetTabGroup>` so we need to throw an error as this component
// can't function without it.
if (!ctx) {
  throw new Error('TabSheetTab components must be used inside TabSheetTabGroup')
}

const isSelected = computed(() => ctx.value === props.value)

const select = () => {
  ctx.value = props.value
}
</script>

<template>
  <button
    class="glp-tab"
    :class="{
      'glp-tab--selected': isSelected,
    }"
    :aria-selected="isSelected ? 'true' : 'false'"
    @click="select"
  >
    <Icon v-if="icon" class="glp-tab__icon" :name="icon" display="none" />
    {{ label }}
  </button>
</template>

<style>
/* -------------------------------- Structure ------------------------------- */
.glp-tab {
  display: inline-flex;
  white-space: nowrap;
  user-select: none;
  padding-top: var(--glp-tabs-item-padding-top, 8px);
  padding-bottom: calc(
    var(--glp-tabs-item-padding-bottom, 12px) -
      var(--tabs-item-size-highlight, 4px)
  );
  padding-inline: var(--glp-tabs-item-padding-horizontal, 8px);
  justify-content: center;
  align-items: center;
  gap: var(--tabs-item-gap, 4px);
  background: var(
    --glp-tab-background,
    var(--glp-tabs-item-default-background, rgba(255, 255, 255, 0))
  );
  border-bottom: var(--tabs-item-size-highlight, 4px) solid
    var(--glp-tab-indicator-color, transparent);
  font-weight: var(
    --glp-tab-font-weight,
    var(--glp-tabs-item-font-weight, 450)
  );
  font-size: var(--glp-tabs-item-font-size, 16px);
  line-height: var(--glp-tabs-item-size-icon, 20px);
}

.glp-tab__icon {
  width: var(--glp-tabs-item-size-icon, 20px);
  height: auto;
}

/* --------------------------------- States --------------------------------- */

.glp-tab--selected {
  --glp-tab-font-weight: 700;
  --glp-tab-indicator-color: var(--glp-tabs-item-selector, #ff7300);
  pointer-events: none;
}

.glp-tab:not([aria-selected='true']):hover {
  --glp-tab-background: var(
    --glp-tabs-item-hover-background,
    rgba(1, 9, 12, 0.04)
  );
}

.glp-tab:not([aria-selected='true']):active {
  --glp-tab-background: var(
    --glp-tabs-item-pressed-background,
    rgba(1, 9, 12, 0.08)
  );
}

.glp-tab:focus-visible {
  border-radius: 4px;
  outline: 3px solid var(--glp-general-color-focus);
  isolation: isolate; /** Ensure focus ring stacks above other elements */
}
</style>
